import {Component, OnInit} from '@angular/core';
import {FormControl, UntypedFormControl} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, map, switchMap, tap} from 'rxjs';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IVacationDaysTable} from 'src/app/interfaces/general-tables.interface';
import {
  ISalaryChanges,
  ISalaryIncrease,
} from 'src/app/interfaces/salary-increase';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {SalaryService} from 'src/app/services/accountant/salary.service';
import {DialogService} from 'src/app/services/dialog.service';
import {
  ITableRowAction,
  UITableHeader,
} from 'src/app/shared/ui-kits/table/table.models';
import {IncreaseSalaryPopupComponent} from './increase-salary-popup/increase-salary-popup.component';
import {VacationPopupComponent} from './vacation-popup/vacation-popup.component';
import {EmployeeService} from 'src/app/services/employee.service';
import {roles} from 'src/app/shared/utils/roles';
import {SalaryRequestStatus} from 'src/app/enums/salary-request-status';
import {ALL_OPTION} from 'src/app/shared/utils/constants';

/**
 * The component for displaying extended data on salary changes
 */
@UntilDestroy()
@Component({
  selector: 'app-salary-extended',
  templateUrl: './salary-extended.component.html',
  styleUrls: ['./salary-extended.component.scss'],
})
export class SalaryExtendedComponent implements OnInit {
  public employeeId!: number;
  public headersSalary: UITableHeader[] = [
    // headers for the salary table
    {key: 'increaseDate', index: 0},
    {key: 'activeFrom', index: 1},
    {key: 'salaryStavka', index: 2},
    {key: 'ratePerHour', index: 3},
    {key: '_168', index: 4},
    {key: 'total', index: 5},
    {key: 'increase', index: 6},
    {key: 'increasePercents', index: 7},
    {key: 'increaseReason', index: 11},
    {key: 'status', index: 14},
  ];
  public dataSalary: ISalaryChanges[] = []; // data for the salary table
  public headersVacation: UITableHeader[] = [
    // headers for the vacation table
    {key: 'startDate', index: 0},
    {key: 'vacationPeriod', index: 1},
    {key: 'providedVacationDays', index: 2},
    {key: 'usedVacationDays', index: 3},
    {key: 'dateOfAnnulment', index: 4},
    {key: 'amountOfDaysToBeAnnuled', index: 5},
    {key: 'accumulatedVacationDaysAtTheMonth', index: 6},
  ];
  public dataVacation: IVacationDaysTable[] = []; // data for the vacation table
  public isAllowedSalaryIncrease = false; // show/hide salary increase button
  public increaseReasonItems: IDropdownObject[] = []; // possible items for increase reasons
  public isAbleToView!: boolean;
  public currEmploymentForm!: IDropdownObject;
  private ALL_SALARY_INCREASE_OPTION: IDropdownObject = {
    name: ALL_OPTION,
    id: Object.keys(SalaryRequestStatus).length.toString(),
  };

  increaseStatusOptions: IDropdownObject[] = Object.keys(SalaryRequestStatus)
    .filter(v => isNaN(Number(v)))
    .map((status, index) => {
      return {
        name: status,
        id: index,
      };
    });

  salaryStatusControl: FormControl<IDropdownObject> = new FormControl();

  get isAccountantDepartment(): boolean {
    return this.access.checkPermissions([...roles.ACCOUNTANT_DEPARTMENT]);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService,
    private salaryService: SalaryService,
    private employeeService: EmployeeService,
    private access: AccessManagementService,
    private router: Router
  ) {
    this.increaseStatusOptions.unshift(this.ALL_SALARY_INCREASE_OPTION);
  }

  ngOnInit(): void {
    this.employeeId = +this.activatedRoute.parent?.snapshot.params['id'];
    const accessData = this.activatedRoute.parent?.snapshot.data['accessData'];
    this.isAbleToView = this.access.salaryIncreaseReadAccess(accessData);

    this.salaryService
      .getAllEmployeeVacations(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(data => (this.dataVacation = data));

    this.employeeService
      .getCurrentEmploymentForm(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(data => (this.currEmploymentForm = data));

    this.salaryStatusControl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(res => this.processIncreaseStatus(res)),
        filter(() => this.isAbleToView),
        switchMap((res: SalaryRequestStatus | null) => {
          return this.salaryService.getSalaryChanges(this.employeeId, {
            status: res,
          });
        })
      )
      .subscribe(data => {
        this.dataSalary = data.salaries ?? [];

        this.isAllowedSalaryIncrease =
          this.access.salaryIncreaseCreateAccess(accessData);
      });

    if (this.isAbleToView)
      this.salaryService
        .getIncreaseSalaryReasons()
        .pipe(untilDestroyed(this))
        .subscribe(data => (this.increaseReasonItems = data));

    this.salaryStatusControl.setValue(this.ALL_SALARY_INCREASE_OPTION);
  }

  /**
   * Opening a modal window for entering a salary increase
   */
  openPopUp() {
    this.dialogService
      .open(IncreaseSalaryPopupComponent, {
        data: {
          increaseReasonItems: this.increaseReasonItems,
          employeeId: this.employeeId,
          employmentForm: this.currEmploymentForm,
        },
        settings: {
          width: '770px',
          height: 'fit-content',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap((body: ISalaryIncrease) =>
          this.salaryService.addIncreaseSalary(body, this.employeeId)
        ),
        tap(() => this.salaryStatusControl.updateValueAndValidity())
      )
      .subscribe();
  }

  showDialog(event: ITableRowAction) {
    this.dialogService
      .open(VacationPopupComponent, {
        data: this.dataVacation[event.index].dateOfAnnulment,
        settings: {},
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => {
          const body = {
            id: this.dataVacation[event.index].id as number,
            annulationDate: data,
          };
          return this.salaryService.updateVacation(body, this.employeeId);
        })
      )
      .subscribe(data => (this.dataVacation = data));
  }

  redirectToSalaryIncreases() {
    this.router.navigateByUrl(`salary/main/${this.employeeId}/increase-info`);
  }

  private processIncreaseStatus(status: IDropdownObject): number | null {
    if (status.name === ALL_OPTION) {
      return null;
    }

    return status.id as number;
  }
}
